import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CircleAlert, CircleCheck, Info } from 'lucide-react';
import { loginAsync, registerAsync } from './state/Actions';
import { useAppDispatch } from '../../state/AppStateProvider';
import apiClient from '../../api/apiClient';
import { API_ENDPOINTS } from '../../api/apiEndpoints';
import { Form, useForm, toast } from '../../ui';
import accountService from '../../services/accountService';
import { hasSession, safeNextPath } from '../../utils/session';
import { getErrorMessage } from '../../utils/apiError';
import Logo from '../../components/site/Logo';
import {
  TextField,
  PasswordField,
  PASSWORD_HINT,
  newPasswordRules,
  matchesField,
  normalizeGithubInput,
  GITHUB_USERNAME_PATTERN,
} from './components/FormFields';
import './authStyle.css';

const MODE = { LOGIN: 'login', REGISTER: 'register', FORGOT: 'forgot', RESET: 'reset' };

const COPY = new Map([
  [
    MODE.LOGIN,
    { title: 'Welcome back', sub: 'Sign in to see your orders, downloads and repository access.' },
  ],
  [
    MODE.REGISTER,
    {
      title: 'Create your account',
      sub: 'You need an account to buy a project and receive its files and repository access.',
    },
  ],
  [
    MODE.FORGOT,
    {
      title: 'Reset your password',
      sub: 'Enter the email or phone number on your account. We email a 6-digit code to the address on the account.',
    },
  ],
  [
    MODE.RESET,
    { title: 'Enter your code', sub: 'Use the 6-digit code from the email and choose a new password.' },
  ],
]);

const cleanPhone = value => String(value || '').replace(/[\s-]/g, '');

const phoneRules = [
  { required: true, message: 'Enter your phone number.' },
  {
    validator: (_, value) =>
      !value || /^\d{10}$/.test(cleanPhone(value))
        ? Promise.resolve()
        : Promise.reject(new Error('Enter a 10-digit mobile number.')),
  },
];

const firstName = name => String(name || '').trim().split(/\s+/)[0] || '';

const FormAlert = ({ tone = 'danger', children }) => {
  const Icon = tone === 'danger' ? CircleAlert : tone === 'success' ? CircleCheck : Info;
  return (
    <div className="pk-alert" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = new URLSearchParams(location.search);
  const next = safeNextPath(params.get('next')) || '/';

  const loginForm = useForm();
  const registerForm = useForm();
  const forgotForm = useForm();
  const resetForm = useForm();

  // Signed-in visitors go straight on; checked once so a fresh sign-in navigates itself.
  const [alreadySignedIn] = useState(() => hasSession());
  const [mode, setMode] = useState(() =>
    params.get('mode') === MODE.REGISTER ? MODE.REGISTER : MODE.LOGIN
  );
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  // GitHub username check for sign-up: idle | checking | verified
  const [github, setGithub] = useState({ status: 'idle', user: null });
  const githubRequest = useRef(0);
  const titleRef = useRef(null);
  const focusTitleOnChange = useRef(false);

  useEffect(() => {
    if (focusTitleOnChange.current) {
      titleRef.current?.focus();
      focusTitleOnChange.current = false;
    }
  }, [mode]);

  const switchMode = (nextMode, { focusTitle = false } = {}) => {
    if (nextMode === mode) return;
    setFormError('');
    setNotice('');
    if (nextMode === MODE.LOGIN || nextMode === MODE.REGISTER) {
      loginForm.resetFields();
      registerForm.resetFields();
      githubRequest.current += 1; // ignore a GitHub check still in flight
      setGithub({ status: 'idle', user: null });
    }
    focusTitleOnChange.current = focusTitle;
    setMode(nextMode);
  };

  const onTabKeyDown = e => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const target = mode === MODE.LOGIN ? MODE.REGISTER : MODE.LOGIN;
    switchMode(target);
    document.getElementById(`auth-tab-${target}`)?.focus();
  };

  // ---------- Sign in ----------
  const handleLogin = async values => {
    if (busy) return;
    setFormError('');
    setBusy(true);
    const result = await loginAsync({
      dispatch,
      phoneNumber: cleanPhone(values.phoneNumber),
      password: values.password,
    });
    setBusy(false);

    if (!result.success) {
      setFormError(result.message);
      return;
    }
    const name = firstName(result.data?.fullName);
    toast.success(name ? `Welcome back, ${name}.` : 'Signed in.');
    navigate(next, { replace: true });
  };

  // ---------- GitHub username ----------
  const setGithubError = message =>
    registerForm.setFields([{ name: 'githubUsername', errors: message ? [message] : [] }]);

  // Resolves to the canonical username when the account exists, otherwise null.
  const verifyGithub = async () => {
    const username = normalizeGithubInput(registerForm.getFieldValue('githubUsername'));
    if (!username) {
      setGithubError('Enter your GitHub username.');
      return null;
    }
    if (!GITHUB_USERNAME_PATTERN.test(username)) {
      setGithubError("That isn't a valid GitHub username. Use letters, numbers and single hyphens.");
      return null;
    }

    const request = ++githubRequest.current;
    setGithub({ status: 'checking', user: null });
    try {
      const response = await apiClient.get(API_ENDPOINTS.GITHUB.SEARCH_USER(encodeURIComponent(username)));
      if (request !== githubRequest.current) return null;
      const result = response.data?.data;
      if (result?.success && result.user?.username) {
        registerForm.setFields([{ name: 'githubUsername', value: result.user.username, errors: [] }]);
        setGithub({ status: 'verified', user: result.user });
        return result.user.username;
      }
      setGithub({ status: 'idle', user: null });
      setGithubError(`We couldn't find a GitHub account called "${username}". Check the spelling on github.com.`);
      return null;
    } catch (error) {
      if (request !== githubRequest.current) return null;
      setGithub({ status: 'idle', user: null });
      setGithubError(getErrorMessage(error, "We couldn't check GitHub right now. Please try again."));
      return null;
    }
  };

  const onGithubEdited = () => {
    githubRequest.current += 1;
    if (github.status !== 'idle') setGithub({ status: 'idle', user: null });
  };

  // ---------- Create account ----------
  const handleRegister = async values => {
    if (busy) return;
    setFormError('');
    setBusy(true);

    const githubUsername = github.status === 'verified' ? values.githubUsername : await verifyGithub();
    if (!githubUsername) {
      setBusy(false);
      setFormError('Check your GitHub username above.');
      return;
    }

    const phoneNumber = cleanPhone(values.phoneNumber);
    const created = await registerAsync({
      dispatch,
      name: values.name.trim(),
      phoneNumber,
      email: values.email.trim(),
      githubUsername,
      password: values.password,
    });
    if (!created.success) {
      setBusy(false);
      setFormError(created.message);
      return;
    }

    // Sign straight in with the same details.
    const login = await loginAsync({ dispatch, phoneNumber, password: values.password });
    setBusy(false);
    if (login.success) {
      const name = firstName(login.data?.fullName || values.name);
      toast.success(name ? `Welcome to Projects Kart, ${name}.` : 'Your account is ready.');
      navigate(next, { replace: true });
      return;
    }
    switchMode(MODE.LOGIN);
    loginForm.setFieldsValue({ phoneNumber });
    setNotice('Your account was created. Please sign in.');
  };

  // ---------- Forgot / reset password ----------
  const openReset = identifier => {
    resetForm.resetFields();
    if (identifier) resetForm.setFieldsValue({ identifier });
    switchMode(MODE.RESET, { focusTitle: true });
  };

  const handleRequestReset = async ({ identifier }) => {
    if (busy) return;
    setFormError('');
    setBusy(true);
    const result = await accountService.requestPasswordReset(identifier.trim());
    setBusy(false);
    if (!result.success) {
      setFormError(result.error);
      return;
    }
    openReset(identifier.trim());
    setNotice(
      result.message ||
        "If an account with an email address matches, we've sent it a code. Accounts without an email need to contact support."
    );
  };

  const handleResetPassword = async ({ identifier, code, newPassword }) => {
    if (busy) return;
    setFormError('');
    setBusy(true);
    const id = identifier.trim();
    const result = await accountService.resetPassword({ identifier: id, code: code.trim(), newPassword });
    setBusy(false);
    if (!result.success) {
      setFormError(result.error);
      return;
    }
    forgotForm.resetFields();
    resetForm.resetFields();
    switchMode(MODE.LOGIN, { focusTitle: true });
    if (/^\d{10}$/.test(cleanPhone(id))) loginForm.setFieldsValue({ phoneNumber: cleanPhone(id) });
    setNotice(result.message || 'Your password has been reset. You can sign in now.');
  };

  if (alreadySignedIn) return <Navigate to={next} replace />;

  const tabbed = mode === MODE.LOGIN || mode === MODE.REGISTER;
  const copy = COPY.get(mode);
  const submitProps = {
    type: 'submit',
    className: 'pk-btn pk-btn--primary pk-btn--block',
    'aria-busy': busy || undefined,
  };

  return (
    <main className="pk-auth">
      <div className="pk-auth__inner">
        <Link to="/" className="pk-auth__brand" aria-label="Projects Kart home">
          <Logo />
        </Link>

        <section className="pk-auth__card" aria-labelledby="auth-title">
          <h1 className="pk-auth__title" id="auth-title" ref={titleRef} tabIndex={-1}>
            {copy.title}
          </h1>
          <p className="pk-auth__sub">{copy.sub}</p>

          {tabbed && (
            <div className="pk-auth__switch" role="tablist" aria-label="Sign in or create an account">
              {[
                { key: MODE.LOGIN, label: 'Sign in' },
                { key: MODE.REGISTER, label: 'Create account' },
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  id={`auth-tab-${tab.key}`}
                  className="pk-auth__tab"
                  aria-selected={mode === tab.key}
                  aria-controls="auth-panel"
                  tabIndex={mode === tab.key ? 0 : -1}
                  onClick={() => switchMode(tab.key)}
                  onKeyDown={onTabKeyDown}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div
            id="auth-panel"
            className="pk-auth__panel"
            role={tabbed ? 'tabpanel' : undefined}
            aria-labelledby={tabbed ? `auth-tab-${mode}` : undefined}
          >
            {notice && <FormAlert tone={mode === MODE.RESET ? 'info' : 'success'}>{notice}</FormAlert>}

            {mode === MODE.LOGIN && (
              <Form form={loginForm} onFinish={handleLogin} className="pk-form">
                <TextField
                  name="phoneNumber"
                  label="Phone number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="10-digit mobile number"
                  rules={phoneRules}
                />
                <PasswordField
                  name="password"
                  label="Password"
                  autoComplete="current-password"
                  rules={[{ required: true, message: 'Enter your password.' }]}
                  labelAside={
                    <button
                      type="button"
                      className="pk-auth__link"
                      onClick={() => {
                        forgotForm.resetFields();
                        switchMode(MODE.FORGOT, { focusTitle: true });
                      }}
                    >
                      Forgot password?
                    </button>
                  }
                />
                {formError && <FormAlert>{formError}</FormAlert>}
                <button {...submitProps}>Sign in</button>
              </Form>
            )}

            {mode === MODE.REGISTER && (
              <Form form={registerForm} onFinish={handleRegister} className="pk-form">
                <TextField
                  name="name"
                  label="Full name"
                  autoComplete="name"
                  rules={[
                    { required: true, whitespace: true, message: 'Enter your full name.' },
                    { min: 2, max: 50, message: 'Use 2 to 50 characters.' },
                  ]}
                />
                <TextField
                  name="phoneNumber"
                  label="Phone number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="10-digit mobile number"
                  hint="You sign in with this number."
                  rules={phoneRules}
                />
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  hint="For receipts, delivery emails and password reset codes."
                  rules={[
                    { required: true, message: 'Enter your email address.' },
                    { type: 'email', message: 'Enter a valid email address.' },
                  ]}
                />
                <TextField
                  name="githubUsername"
                  label="GitHub username"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="octocat"
                  hint="Repository access for your purchases is sent to this GitHub account."
                  rules={[{ required: true, whitespace: true, message: 'Enter your GitHub username.' }]}
                  onValueChange={onGithubEdited}
                  after={
                    <button
                      type="button"
                      className="pk-btn pk-btn--secondary pk-btn--sm"
                      onClick={verifyGithub}
                      aria-busy={github.status === 'checking' || undefined}
                    >
                      {github.status === 'verified' ? (
                        <>
                          <Check aria-hidden="true" /> Verified
                        </>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  }
                />
                {github.status === 'verified' && github.user && (
                  <div className="pk-auth__verified" role="status">
                    {github.user.avatarUrl ? (
                      <img src={github.user.avatarUrl} alt="" width="36" height="36" />
                    ) : (
                      <CircleCheck aria-hidden="true" />
                    )}
                    <div>
                      <strong>@{github.user.username}</strong>
                      <span>{github.user.name ? `${github.user.name} · ` : ''}GitHub account found</span>
                    </div>
                  </div>
                )}
                <PasswordField
                  name="password"
                  label="Password"
                  autoComplete="new-password"
                  hint={PASSWORD_HINT}
                  rules={newPasswordRules}
                />
                {formError && <FormAlert>{formError}</FormAlert>}
                <button {...submitProps}>Create account</button>
              </Form>
            )}

            {mode === MODE.FORGOT && (
              <Form form={forgotForm} onFinish={handleRequestReset} className="pk-form">
                <TextField
                  name="identifier"
                  label="Email or phone number"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  rules={[{ required: true, whitespace: true, message: 'Enter your email or phone number.' }]}
                />
                {formError && <FormAlert>{formError}</FormAlert>}
                <button {...submitProps}>Send code</button>
                <div className="pk-auth__links">
                  <button
                    type="button"
                    className="pk-auth__link"
                    onClick={() => openReset(forgotForm.getFieldValue('identifier')?.trim())}
                  >
                    I already have a code
                  </button>
                  <button
                    type="button"
                    className="pk-auth__link"
                    onClick={() => switchMode(MODE.LOGIN, { focusTitle: true })}
                  >
                    Back to sign in
                  </button>
                </div>
              </Form>
            )}

            {mode === MODE.RESET && (
              <Form form={resetForm} onFinish={handleResetPassword} className="pk-form">
                <TextField
                  name="identifier"
                  label="Email or phone number"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  rules={[{ required: true, whitespace: true, message: 'Enter your email or phone number.' }]}
                />
                <TextField
                  name="code"
                  label="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  rules={[
                    { required: true, message: 'Enter the code from the email.' },
                    { pattern: /^\s*\d{6}\s*$/, message: 'The code has 6 digits.' },
                  ]}
                />
                <PasswordField
                  name="newPassword"
                  label="New password"
                  autoComplete="new-password"
                  hint={PASSWORD_HINT}
                  rules={newPasswordRules}
                />
                <PasswordField
                  name="confirmPassword"
                  label="Confirm new password"
                  autoComplete="new-password"
                  rules={[{ required: true, message: 'Enter the new password again.' }, matchesField('newPassword')]}
                />
                {formError && <FormAlert>{formError}</FormAlert>}
                <button {...submitProps}>Set new password</button>
                <div className="pk-auth__links">
                  <button
                    type="button"
                    className="pk-auth__link"
                    onClick={() => {
                      forgotForm.setFieldsValue({ identifier: resetForm.getFieldValue('identifier') || '' });
                      switchMode(MODE.FORGOT, { focusTitle: true });
                    }}
                  >
                    Send a new code
                  </button>
                  <button
                    type="button"
                    className="pk-auth__link"
                    onClick={() => switchMode(MODE.LOGIN, { focusTitle: true })}
                  >
                    Back to sign in
                  </button>
                </div>
              </Form>
            )}
          </div>
        </section>

        <nav className="pk-auth__foot" aria-label="More">
          <Link to="/">
            <ArrowLeft aria-hidden="true" /> Back to the store
          </Link>
          <span className="pk-auth__foot-links">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Help</Link>
          </span>
        </nav>
      </div>
    </main>
  );
};

export default AuthPage;
